import { useCallback, useRef, useState } from "react";
import { useLazyGetProvisioningStatusQuery } from "../services/api/onboarding.api";
import { ICompanyProvisioningStatus } from "../services/types/onboardint.types";
import { TProvisioningStatus } from "../services/types/users.types";
import { tWithKeyAttr as t } from "../i18nHelper";
import {
  delay,
  FetchProvisioningStatus,
  getProvisioningErrorMessage,
  normalizeProvisioningStatus,
  pollProvisioningStatus,
  ProvisioningFailedError,
  PROVISIONING_SUCCESS_DISPLAY_MS,
  shouldWaitForProvisioning,
} from "../utils/companyProvisioning";

export type ProvisioningOverlayMode = "processing" | "success" | "failed";

export interface ProvisioningOverlayState {
  visible: boolean;
  mode: ProvisioningOverlayMode;
  message: string;
  errorMessage: string;
}

const getDefaultProvisioningMessage = () => t("COMPANY_PROVISIONING_MESSAGE");

const getDefaultProvisioningSuccessMessage = () =>
  t("COMPANY_PROVISIONING_SUCCESS_MESSAGE");

const initialOverlayState: ProvisioningOverlayState = {
  visible: false,
  mode: "processing",
  message: getDefaultProvisioningMessage(),
  errorMessage: "",
};

export interface ProvisioningSource {
  guid?: string;
  provisioningStatus?: TProvisioningStatus | null;
  message?: string;
  errorMessage?: string;
}

export const useCompanyProvisioning = () => {
  const [getProvisioningStatus] = useLazyGetProvisioningStatusQuery();
  const [overlay, setOverlay] = useState<ProvisioningOverlayState>(initialOverlayState);
  const fetchStatusRef = useRef<FetchProvisioningStatus | null>(null);

  const fetchStatus = useCallback<FetchProvisioningStatus>(
    async (guid: string) => {
      return getProvisioningStatus(guid).unwrap();
    },
    [getProvisioningStatus]
  );

  fetchStatusRef.current = fetchStatus;

  const hideOverlay = useCallback(() => {
    setOverlay(initialOverlayState);
  }, []);

  const resolveWithSuccess = useCallback(async () => {
    setOverlay({
      visible: true,
      mode: "success",
      message: getDefaultProvisioningSuccessMessage(),
      errorMessage: "",
    });
    await delay(PROVISIONING_SUCCESS_DISPLAY_MS);
    hideOverlay();
  }, [hideOverlay]);

  const waitForProvisioning = useCallback(
    async (
      source: ProvisioningSource,
      isNewCompany: boolean
    ): Promise<boolean> => {
      const status = normalizeProvisioningStatus(source.provisioningStatus);

      if (!shouldWaitForProvisioning(isNewCompany, status)) {
        if (isNewCompany && status === "failed") {
          setOverlay({
            visible: true,
            mode: "failed",
            message: source.message || getDefaultProvisioningMessage(),
            errorMessage: getProvisioningErrorMessage(source),
          });
          return false;
        }
        return true;
      }

      if (!source.guid) {
        setOverlay({
          visible: true,
          mode: "failed",
          message: source.message || getDefaultProvisioningMessage(),
          errorMessage: getProvisioningErrorMessage(source),
        });
        return false;
      }

      setOverlay({
        visible: true,
        mode: "processing",
        message: source.message || getDefaultProvisioningMessage(),
        errorMessage: "",
      });

      try {
        await pollProvisioningStatus(source.guid, fetchStatusRef.current!, {
          onMessageUpdate: (message) => {
            setOverlay((prev) =>
              prev.visible && prev.mode === "processing"
                ? { ...prev, message }
                : prev
            );
          },
        });
        await resolveWithSuccess();
        return true;
      } catch (error) {
        const errorMessage =
          error instanceof ProvisioningFailedError
            ? error.message
            : getProvisioningErrorMessage(source);

        setOverlay({
          visible: true,
          mode: "failed",
          message: source.message || getDefaultProvisioningMessage(),
          errorMessage,
        });
        return false;
      }
    },
    [hideOverlay, resolveWithSuccess]
  );

  const retryProvisioning = useCallback(
    async (guid?: string): Promise<boolean> => {
      if (!guid) {
        setOverlay({
          visible: true,
          mode: "failed",
          message: getDefaultProvisioningMessage(),
          errorMessage: "",
        });
        return false;
      }

      try {
        // Check quietly first - the overlay is a loader, so it should only
        // appear if there's actually something to wait for.
        const statusResponse: ICompanyProvisioningStatus =
          await fetchStatusRef.current!(guid);
        const status = normalizeProvisioningStatus(
          statusResponse.provisioningStatus
        );

        if (status === "failed") {
          setOverlay({
            visible: true,
            mode: "failed",
            message:
              statusResponse.message || getDefaultProvisioningMessage(),
            errorMessage: getProvisioningErrorMessage(statusResponse),
          });
          return false;
        }

        if (status === "complete") {
          // Already done - nothing to load, so there's nothing to show.
          hideOverlay();
          return true;
        }

        setOverlay({
          visible: true,
          mode: "processing",
          message: statusResponse.message || getDefaultProvisioningMessage(),
          errorMessage: "",
        });

        await pollProvisioningStatus(guid, fetchStatusRef.current!, {
          onMessageUpdate: (message) => {
            setOverlay((prev) =>
              prev.visible && prev.mode === "processing"
                ? { ...prev, message }
                : prev
            );
          },
        });
        await resolveWithSuccess();
        return true;
      } catch (error) {
        const errorMessage =
          error instanceof ProvisioningFailedError ? error.message : "";

        setOverlay({
          visible: true,
          mode: "failed",
          message: getDefaultProvisioningMessage(),
          errorMessage,
        });
        return false;
      }
    },
    [hideOverlay, resolveWithSuccess]
  );

  return {
    overlay,
    waitForProvisioning,
    retryProvisioning,
    hideOverlay,
  };
};
